import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPaymentStatus } from '../lib/api';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      navigate('/org/dashboard');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await getPaymentStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div className="bg-white border border-zinc-200 p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-zinc-400 animate-spin mb-4" />
            <h1 className="text-xl font-semibold text-zinc-900 mb-2">Processing Payment</h1>
            <p className="text-zinc-600">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">Payment Successful!</h1>
            <p className="text-zinc-600 mb-6">
              Your payment has been confirmed. You can now proceed to complete the required assessments.
            </p>
            <Button onClick={() => navigate('/org/dashboard')} className="w-full" data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-red-500 text-2xl">✕</span>
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">Payment Failed</h1>
            <p className="text-zinc-600 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Button onClick={() => navigate('/org/dashboard')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-yellow-500 text-2xl">!</span>
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">Payment Status Unknown</h1>
            <p className="text-zinc-600 mb-6">
              We couldn't confirm your payment status. Please check your dashboard or contact support.
            </p>
            <Button onClick={() => navigate('/org/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-red-500 text-2xl">!</span>
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">Error</h1>
            <p className="text-zinc-600 mb-6">
              There was an error checking your payment status. Please check your dashboard.
            </p>
            <Button onClick={() => navigate('/org/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
